import type { SupWeb3Address } from "@/types";
import { useMemo } from "react";

import useApi from "@/hooks/api/useApi";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";

interface PiggyTokenBalanceResponse {
  user: SupWeb3Address;
  piggyBalance: string;
  piggyEthBalance: string;
  depositedAmount: string;
  availableAmount: string;
  rewardsAPY: number;
}
export interface PiggyTokenBalance {
  user: SupWeb3Address;
  piggyBalance: bigint;
  piggyEthBalance: bigint;
  depositedAmount: bigint;
  availableAmount: bigint;
  rewardsAPY: number;
}

/**
 * Hook to fetch piggy, piggy-eth (aka LP), deposited, available balances and rewards APY
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/30355904-ed2e1607-c9a3-44f7-ba08-935f74c96d3f?action=share&source=copy-link&creator=26633220&active-environment=9faa12dd-452a-4b02-81a7-eced0431d716
 */
export default function useTokenDistributionBalance(force: boolean = false) {
  const userAddress = useWeb3UserAddress();

  const path = useMemo(() => `/token-distribution/balance/${userAddress}/?force=${force}`, [userAddress, force]);

  const { data, isLoading, isError, error, mutate } = useApi<PiggyTokenBalance, PiggyTokenBalanceResponse>(
    {
      path,
      api: "v2",
    },
    {
      enabled: !!userAddress,
      strategy: "fully-cached",
      select: response => ({
        user: response.user,
        piggyBalance: BigInt(response.piggyBalance),
        piggyEthBalance: BigInt(response.piggyEthBalance),
        depositedAmount: BigInt(response.depositedAmount),
        availableAmount: BigInt(response.availableAmount),
        rewardsAPY: response.rewardsAPY,
      }),
    }
  );

  return {
    data,
    isLoading,
    isError,
    error,
    mutate,
  };
}
